import { Component, computed, input } from '@angular/core';
import { DecimalPipe, JsonPipe } from '@angular/common';
import { NgbDate, NgbPopoverModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { SkeletonLoaderComponent } from '../../../../common/components/skeleton-loader/skeleton-loader';
import { PRICE_LOADER_CONFIG, RATE_CALENDAR_SETTINGS } from '../../../../common/configs/common.config';
import { ShortenNumberPipe } from '../../../../common/pipes/shorten-number-pipe.pipe';
import { StoreService } from '../../../../store/store.service';
import { CalendarService } from '../../../../common/services/calendar.service';
import { OverlayDirective } from '../../../../common/directives/overlay.directive';
import { CommonService } from '../../../../common/services/common.service';

@Component({
  standalone: true,
  imports: [
    JsonPipe,
    DecimalPipe,
    ShortenNumberPipe,
    SkeletonLoaderComponent,
    OverlayDirective,
    NgbTooltipModule,
    NgbPopoverModule
  ],
  selector: 'app-calendar-day',
  templateUrl: './calendar-day.component.html',
  host: {
    class: 'custom-day',
    '[class.focused]': 'isFocused()',
    '[class.hovered]': 'isHovered()',
    '[class.startRange]': 'isStartRange()',
    '[class.inRange]': 'isInRange()',
    '[class.endRange]': 'isEndRange()',
    '[class.is-loading]': 'isLoading()',
    '[class.unavailable]': `
      hasDayRateData() &&
      !dayRateData()?.available &&
      !dayRateData()?.isClosedToArrival &&
      !dayRateData()?.isClosedToDeparture &&
      !dayRateData()?.altPropertyAvailable
    `,
    '[class.complimentary]': `
      hasDayRateData() &&
      dayRateData()?.available &&
      dayRateData()?.isComplimentary
    `,
    '[class.no-check-in]': '!isStartRange() && !isInRange() && !isEndRange() && dayRateData()?.isClosedToArrival',
    '[class.no-check-out]': '!isStartRange() && !isInRange() && dayRateData()?.isClosedToDeparture',
  }
})
export class CalendarDayComponent {
  // Required Inputs
  public readonly minDate = input.required<NgbDate>();
  public readonly date = input.required<NgbDate>();
  public readonly hasDayRateData = input.required<boolean>();
  public readonly dayRateData = input.required<any>();
  public readonly isFocused = input.required<boolean>();
  public readonly isHovered = input.required<boolean>();
  public readonly isStartRange = input.required<boolean>();
  public readonly isInRange = input.required<boolean>();
  public readonly isEndRange = input.required<boolean>();
  public readonly isNotAvailable = input.required<boolean>();
  public readonly isComplimentary = input.required<boolean>();
  public readonly isLoading = input.required<boolean>();
  public readonly priceLoaderConfig = input.required<typeof PRICE_LOADER_CONFIG>();

  // Constants
  public readonly Intl_NumberFormatOptions = RATE_CALENDAR_SETTINGS.Intl_NUMBER_FORMAT_OPTIONS;

  // Signals
  public readonly localeObj = this.storeSvc.userStore.localeObj;
  public readonly currencySymbol = this.storeSvc.userStore.currency.symbol;
  public readonly langTag = this.storeSvc.userStore.langObj.code;
  public readonly isUpperCaseLabel = this.storeSvc.appStore.isUpperCaseLabel;

  // Computed Signals
  public readonly displayShortFormatPrice = computed(
    () => !this.storeSvc.userStore.propertyInfo().displayShortFormatPrice
  );
  public readonly dateStr = computed(() => {
    const date = this.date();
    return date.year + '-' + date.month + '-' + date.day;
  });

  constructor(
    public readonly storeSvc: StoreService,
    public readonly calendarSvc: CalendarService,
    public readonly commonSvc: CommonService,
  ) { }

  public get minLOSTooltip() {
    return this.commonSvc.fillMessage(
      this.localeObj()?.tf_1_Calendar_rateCalender_minLosTooltip,
      [this.dayRateData()?.minLos]
    );
  }
}